import type { AdaptiveSubmit, JsonObject } from 'components/AdaptiveCard/AdaptiveCardRenderer';
import { TFunction } from 'i18next';
import {
  CopilotConversationContentBlock,
  CopilotConversationItem,
  CopilotConversionItemType,
} from 'models/copilot/copilotConversation';
import { CopilotFieldRef } from 'models/copilot/copilotField';
import { CopilotPanelRuntime } from 'models/copilot/copilotPanelContextDefinition';
import { EntityTypes } from 'models/entity';
import { textToHtmlParagraphs } from 'utils/html';

// The card the agent presents for a text field (`field_proposal_present` on the API): its submit
// actions carry this action name and a decision, its payload the proposed text.
const fieldProposalAction = 'fieldProposal';

type FieldProposalPayload = {
  action?: string;
  entityType: EntityTypes;
  entityId: number;
  field: string;
  isHtml: boolean;
  text: string;
};

/**
 * Answers the field proposal card, whichever usage is active: a text field binds its actions from any
 * scene, so the card belongs to the shell and not to a usage. Insert and Replace hand the text to the
 * editor that binds the field through the result bus; the editor applies it unsaved and takes focus.
 * Discard only closes the matter. There is nothing to ask the server: the pushed Copilot item makes the
 * card no longer the last one, which is what disables it. Returns false for any other card.
 */
export const handleFieldProposalSubmit = (
  runtime: CopilotPanelRuntime,
  block: CopilotConversationContentBlock,
  payload: AdaptiveSubmit<JsonObject>,
  t: TFunction<string[]>,
): boolean => {
  if (payload.actionData.action !== fieldProposalAction) return false;

  const proposal = block.payload as FieldProposalPayload | undefined;
  const decision = payload.actionData.decision;
  if (!proposal || typeof decision !== 'string') return false;

  if (decision === 'insert' || decision === 'replace') {
    const ref: CopilotFieldRef = { entityType: proposal.entityType, entityId: proposal.entityId, field: proposal.field };
    const html = proposal.isHtml ? proposal.text : textToHtmlParagraphs(proposal.text);
    runtime.resultBus.publishFieldProposal(ref, html, decision);
  }

  const item = new CopilotConversationItem();
  item.type = CopilotConversionItemType.Copilot;
  if (decision === 'insert') {
    item.response = t('copilot:FieldProposal.Inserted');
  } else if (decision === 'replace') {
    item.response = t('copilot:FieldProposal.Replaced');
  } else {
    item.response = t('copilot:FieldProposal.Discarded');
  }
  runtime.conversation.thread.push(item);
  runtime.setConversation(runtime.conversation.clone());

  return true;
};
